/**
 * @fileoverview Shared storage health service for the FireSight local-first PWA.
 * @module @firesquirrel/local-db/storage/storageHealth
 *
 * Responsibilities:
 * - Provide checkStorageHealth() to estimate IndexedDB quota and usage.
 * - Provide ensureStoragePersistence() to request browser persistence.
 * - Provide useStorageHealthPolling() React hook for automatic 30 s polling.
 *
 * Dependencies:
 * - React (useSyncExternalStore, useEffect, useRef)
 * - ./persistenceContract — shared constants, thresholds, and event names
 *
 * Architecture:
 * - THIS IS THE SINGLE SOURCE OF TRUTH for storage health across all apps.
 * - tenant-web, system-web, and mobile-pwa MUST all import from here.
 * - App-level storagePersistence.ts files must NOT re-implement these functions.
 * - The module-singleton storageHealthStore ensures one polling loop per app
 *   bundle regardless of how many components subscribe.
 *
 * Notes:
 * - _loggedOnce prevents console spam per event per app-bundle session.
 * - Polling requests persistence on every cycle so any Chrome engagement
 *   grant is detected immediately on the next 30 s tick.
 * - Every navigator.storage API call is raced against STORAGE_API_TIMEOUT_MS
 *   so a browser that never settles (Safari Private Browsing, locked-down
 *   WebViews) degrades to an explicit handled failure instead of hanging boot.
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import {
  APP_STORAGE_CAP_BYTES,
  STORAGE_PERSISTENCE_EVENTS,
  STORAGE_PERSISTENCE_THRESHOLDS,
} from './persistenceContract';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract PersistenceOutcome
 *
 * Purpose:
 * - Discriminated union of all possible outcomes from ensureStoragePersistence.
 *
 * Values:
 * - 'already-persisted': navigator.storage.persisted() returned true before requesting.
 * - 'granted': navigator.storage.persist() returned true.
 * - 'denied': navigator.storage.persist() returned false (browser policy).
 * - 'unsupported': Storage Persistence API not available on this platform.
 * - 'error': An unexpected exception was thrown during the request.
 */
export type PersistenceOutcome =
  | 'already-persisted'
  | 'granted'
  | 'denied'
  | 'unsupported'
  | 'error';

/**
 * @contract PersistenceHandshakeResult
 *
 * Purpose:
 * - Structured result returned by ensureStoragePersistence.
 *
 * Fields:
 * - outcome: Discriminated outcome string.
 * - granted: true if storage is or will be persisted (already-persisted | granted).
 *
 * Rules:
 * - granted is ALWAYS derived from outcome — never set independently.
 *
 * Sync:
 * - Result is stored in StorageHealthSnapshot.persistence for UI consumption.
 */
export interface PersistenceHandshakeResult {
  outcome: PersistenceOutcome;
  granted: boolean;
}

/**
 * @contract StorageHealth
 *
 * Purpose:
 * - Point-in-time snapshot of IndexedDB quota usage.
 *
 * Fields:
 * - usage: Bytes currently consumed by IndexedDB.
 * - quota: Effective quota cap (min of browser estimate and APP_STORAGE_CAP_BYTES).
 * - percentUsed: usage / quota * 100.
 * - isPersisted: current navigator.storage.persisted() value at time of check.
 *
 * Rules:
 * - quota is always capped at APP_STORAGE_CAP_BYTES (2 GB).
 * - isPersisted reflects the state BEFORE any persist() request in the same cycle.
 */
export interface StorageHealth {
  usage: number;
  quota: number;
  percentUsed: number;
  isPersisted: boolean;
}

/**
 * @contract StorageHealthSnapshot
 *
 * Purpose:
 * - Full snapshot returned by useStorageHealthPolling.
 *
 * Fields:
 * - health: Latest storage usage and quota data.
 * - persistence: Latest persistence handshake result, or null if first poll pending.
 * - error: Error message from last failed poll cycle, or null.
 * - lastUpdated: ISO timestamp of most recent successful poll, or null.
 * - loading: true only on the very first poll before any data is available.
 */
export interface StorageHealthSnapshot {
  health: StorageHealth;
  persistence: PersistenceHandshakeResult | null;
  error: string | null;
  lastUpdated: string | null;
  loading: boolean;
}

// ---------------------------------------------------------------------------
// Logging — deduplicated per event per app-bundle session
// ---------------------------------------------------------------------------

const _loggedOnce = new Set<string>();
function logStorageEvent(event: string, details: Record<string, string | number> = {}): void {
  if (_loggedOnce.has(event)) return;
  _loggedOnce.add(event);
  // Suppress storage persistence denial logs in dev mode — browsers routinely deny
  // persistence in dev, and the UI already handles this gracefully by not showing
  // the warning modal when appEnv === 'dev'
  if (event === STORAGE_PERSISTENCE_EVENTS.persistDenied) {
    try {
      // Check if we're in dev mode by looking at the web config
      const appEnv = typeof window !== 'undefined' && (window as any).__WEB_CONFIG__?.appEnv;
      if (appEnv === 'dev') return;
    } catch {
      // If we can't determine the env, log anyway
    }
  }

  console.info('[storage]', event, details);
}

// ---------------------------------------------------------------------------
// Core functions
// ---------------------------------------------------------------------------

const STORAGE_API_TIMEOUT_MS = 3_000;

/**
 * @errorHandling withStorageApiTimeout
 * Strategy:    Race a browser Storage API promise against a bounded timer so a
 *              promise that never settles becomes a rejection instead of a hang.
 * User Impact: Startup and health checks always complete; a stalled browser API
 *              degrades to an explicit 'error' outcome rather than a stuck splash.
 * Logging:     REQUIRED — callers log and emit events for the rejection.
 */
async function withStorageApiTimeout<T>(operation: Promise<T>, operationName: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(`${operationName} did not settle within ${STORAGE_API_TIMEOUT_MS}ms`)),
      STORAGE_API_TIMEOUT_MS,
    );
  });

  try {
    return await Promise.race([operation, timeout]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/**
 * @process ensureStoragePersistence
 *
 * Steps:
 * 1. Check if Storage Persistence API is available; return 'unsupported' if not.
 * 2. Call navigator.storage.persisted() — if already granted, return early.
 * 3. Call navigator.storage.persist() and return the browser's decision.
 *
 * Inputs:
 * - reason: Optional label for logging (e.g. 'health-poll', 'auth-success').
 *
 * Outputs:
 * - PersistenceHandshakeResult with outcome and granted flag.
 *
 * Failure Handling:
 * - Any exception (including a Storage API timeout) is caught and returned as
 *   outcome 'error', granted false.
 *
 * Idempotency:
 * - REQUIRED — safe to call on every poll cycle.
 */
export async function ensureStoragePersistence(reason = 'startup'): Promise<PersistenceHandshakeResult> {
  if (!navigator.storage?.persist) {
    logStorageEvent(STORAGE_PERSISTENCE_EVENTS.persistUnsupported, { reason });
    return { outcome: 'unsupported', granted: false };
  }
  try {
    const alreadyPersisted = await withStorageApiTimeout(
      navigator.storage.persisted(),
      'navigator.storage.persisted()',
    );
    if (alreadyPersisted) {
      logStorageEvent(STORAGE_PERSISTENCE_EVENTS.persistAlreadyGranted, { reason });
      return { outcome: 'already-persisted', granted: true };
    }
    const granted = await withStorageApiTimeout(
      navigator.storage.persist(),
      'navigator.storage.persist()',
    );
    logStorageEvent(
      granted ? STORAGE_PERSISTENCE_EVENTS.persistGranted : STORAGE_PERSISTENCE_EVENTS.persistDenied,
      { reason },
    );
    return { outcome: granted ? 'granted' : 'denied', granted };
  } catch (err) {
    logStorageEvent(STORAGE_PERSISTENCE_EVENTS.persistError, {
      reason,
      error: err instanceof Error ? err.message : 'unknown',
    });
    return { outcome: 'error', granted: false };
  }
}

/**
 * @process checkStorageHealth
 *
 * Steps:
 * 1. Check navigator.storage.estimate() availability.
 * 2. Estimate current usage and quota; cap quota at APP_STORAGE_CAP_BYTES.
 * 3. Log warning/critical events if thresholds are exceeded.
 *
 * Inputs:
 * - none
 *
 * Outputs:
 * - StorageHealth snapshot with usage, quota, percentUsed, isPersisted.
 *
 * Failure Handling:
 * - Exception (including a Storage API timeout) returns zeroed StorageHealth
 *   with isPersisted false.
 *
 * Idempotency:
 * - REQUIRED — read-only query, safe to call repeatedly.
 */
export async function checkStorageHealth(): Promise<StorageHealth> {
  if (!navigator.storage?.estimate) {
    logStorageEvent(STORAGE_PERSISTENCE_EVENTS.estimateUnsupported);
    return { usage: 0, quota: 0, percentUsed: 0, isPersisted: false };
  }
  try {
    const [estimate, persisted] = await Promise.all([
      withStorageApiTimeout(navigator.storage.estimate(), 'navigator.storage.estimate()'),
      navigator.storage.persisted
        ? withStorageApiTimeout(navigator.storage.persisted(), 'navigator.storage.persisted()')
        : Promise.resolve(false),
    ]);
    const usage = estimate.usage ?? 0;
    const quota = Math.min(estimate.quota ?? APP_STORAGE_CAP_BYTES, APP_STORAGE_CAP_BYTES);
    const percentUsed = quota > 0 ? (usage / quota) * 100 : 0;

    if (percentUsed >= STORAGE_PERSISTENCE_THRESHOLDS.critical) {
      logStorageEvent(STORAGE_PERSISTENCE_EVENTS.estimateCritical, {
        percentUsed: Number(percentUsed.toFixed(2)),
      });
    } else if (percentUsed >= STORAGE_PERSISTENCE_THRESHOLDS.warning) {
      logStorageEvent(STORAGE_PERSISTENCE_EVENTS.estimateWarning, {
        percentUsed: Number(percentUsed.toFixed(2)),
      });
    }

    return { usage, quota, percentUsed, isPersisted: persisted };
  } catch (err) {
    logStorageEvent(STORAGE_PERSISTENCE_EVENTS.estimateError, {
      error: err instanceof Error ? err.message : 'unknown',
    });
    return { usage: 0, quota: 0, percentUsed: 0, isPersisted: false };
  }
}

// ---------------------------------------------------------------------------
// Polling store — module singleton, shared across all subscribers in one app
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 30_000;

/**
 * @storage StorageHealthPollingStore
 *
 * Type: Memory (useSyncExternalStore-compatible observer)
 *
 * Schema:
 * - snapshot: StorageHealthSnapshot
 * - subscribers: Set of React re-render notifiers
 * - pollingClients: Set of consumer IDs controlling the timer
 *
 * Sync Role:
 * - Module singleton; one polling loop per app bundle.
 * - Fires once immediately on first subscriber; repeats every 30 s if any
 *   polling client is active.
 */
class StorageHealthPollingStore {
  private snapshot: StorageHealthSnapshot = {
    health: { usage: 0, quota: 0, percentUsed: 0, isPersisted: false },
    persistence: null,
    error: null,
    lastUpdated: null,
    loading: false,
  };

  private subscribers = new Set<() => void>();
  private pollingClients = new Set<string>();
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;

  private readonly handleVisibilityChange = () => {
    if (!this.pollingClients.size) return;
    void this.refresh();
  };

  getSnapshot = (): StorageHealthSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.subscribers.add(listener);
    if (this.subscribers.size === 1 && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
    if (!this.snapshot.lastUpdated && !this.snapshot.loading) {
      void this.refresh();
    }
    return () => {
      this.subscribers.delete(listener);
      if (!this.subscribers.size && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      }
    };
  };

  setPollingEnabled(id: string, enabled: boolean): void {
    if (enabled) {
      this.pollingClients.add(id);
      this.scheduleNext(0);
      return;
    }
    this.pollingClients.delete(id);
    this.ensureTimerState();
  }

  clearPollingEnabled(id: string): void {
    this.pollingClients.delete(id);
    this.ensureTimerState();
  }

  refresh = async (reason = 'health-poll'): Promise<void> => {
    if (this.inFlight) return this.inFlight;

    const shouldShowLoading = !this.snapshot.lastUpdated;
    if (shouldShowLoading && !this.snapshot.loading) {
      this.setSnapshot({ ...this.snapshot, loading: true });
    }

    this.inFlight = (async () => {
      try {
        const [health, persistence] = await Promise.all([
          checkStorageHealth(),
          ensureStoragePersistence(reason),
        ]);
        this.setSnapshot({
          health,
          persistence,
          error: null,
          lastUpdated: new Date().toISOString(),
          loading: false,
        });
      } catch (error) {
        this.setSnapshot({
          ...this.snapshot,
          error: error instanceof Error ? error.message : 'Unknown error',
          loading: false,
        });
      } finally {
        this.inFlight = null;
        if (this.pollingClients.size) {
          this.scheduleNext();
        }
      }
    })();

    return this.inFlight;
  };

  private emit(): void {
    this.subscribers.forEach((listener) => listener());
  }

  private setSnapshot(next: StorageHealthSnapshot): void {
    this.snapshot = next;
    this.emit();
  }

  private ensureTimerState(): void {
    if (!this.pollingClients.size) {
      this.clearTimer();
      return;
    }
    this.scheduleNext();
  }

  private clearTimer(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private scheduleNext(delayMs?: number): void {
    this.clearTimer();
    if (!this.pollingClients.size) return;
    const timeout = delayMs ?? POLL_INTERVAL_MS;
    this.timerId = setTimeout(() => {
      this.timerId = null;
      void this.refresh();
    }, timeout);
  }
}

const storageHealthStore = new StorageHealthPollingStore();
let _pollerIdCounter = 0;

// ---------------------------------------------------------------------------
// React hook — identical API in all three apps
// ---------------------------------------------------------------------------

/**
 * @hook useStorageHealthPolling
 *
 * Purpose:
 * - Subscribe to the module-singleton StorageHealthPollingStore.
 * - Optionally enable the 30 s automatic polling loop.
 *
 * Returns:
 * - health: Latest StorageHealth snapshot.
 * - persistence: Latest PersistenceHandshakeResult or null.
 * - error: Last poll error message or null.
 * - lastUpdated: ISO timestamp of last successful poll or null.
 * - loading: true only before the first poll resolves.
 * - refresh: Function to trigger an immediate poll (accepts optional reason string).
 *
 * Side Effects:
 * - Registers/unregisters a polling client ID with the store on mount/unmount.
 * - First subscriber also attaches a visibilitychange listener.
 *
 * Offline Behavior:
 * - Health check and persistence request run regardless of online status.
 */
export function useStorageHealthPolling(autoRefresh = true) {
  const pollerIdRef = useRef<string>();
  if (!pollerIdRef.current) {
    pollerIdRef.current = `storage-health-poller-${++_pollerIdCounter}`;
  }

  const snapshot = useSyncExternalStore(
    storageHealthStore.subscribe,
    storageHealthStore.getSnapshot,
    storageHealthStore.getSnapshot,
  );

  useEffect(() => {
    const pollerId = pollerIdRef.current!;
    storageHealthStore.setPollingEnabled(pollerId, autoRefresh);
    return () => {
      storageHealthStore.clearPollingEnabled(pollerId);
    };
  }, [autoRefresh]);

  return {
    ...snapshot,
    refresh: storageHealthStore.refresh,
  };
}
